import { clearIdleTimeoutOverride } from '@/lib/idleTimeoutOverride';
import { isSignOutStaleAgainstNewerSignIn } from '@/lib/sessionExpiry';
import { withTimeout } from '@/lib/withTimeout';

export const AUTH_STORAGE_KEY = 'clinio-v2-auth';
export const SIGN_OUT_TIMEOUT_MS = 2500;
export const AUTH_INIT_TIMEOUT_MS = 8000;
export const SIGN_IN_TIMEOUT_MS = 15_000;

export interface SignOutRemoteResult {
  error: { message?: string } | null;
}

export interface PerformSignOutDeps {
  signOutRemote: (scope: 'local') => Promise<SignOutRemoteResult>;
  clearQueryCache: () => void;
  clearAuthStorage?: () => void;
  timeoutMs?: number;
  /**
   * When this changes during the call, a newer sign-in won the race — do not
   * force-remove `clinio-v2-auth` or the new session is wiped.
   */
  authGeneration?: () => number;
}

export function clearDefaultAuthStorage(): void {
  // A sign-in that landed after this logout started owns the storage key.
  if (isSignOutStaleAgainstNewerSignIn()) return;
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // localStorage not available
  }
}

/**
 * Never hangs and never throws. Local cache / storage are cleared even when
 * the GoTrue logout request is already dead (expired refresh token, 401, offline).
 */
export async function performSignOut(deps: PerformSignOutDeps): Promise<void> {
  const timeoutMs = deps.timeoutMs ?? SIGN_OUT_TIMEOUT_MS;
  const clearAuthStorage = deps.clearAuthStorage ?? clearDefaultAuthStorage;
  const generationAtStart = deps.authGeneration?.() ?? 0;

  const clearStorageIfSameGeneration = () => {
    if ((deps.authGeneration?.() ?? 0) !== generationAtStart) return;
    try {
      clearAuthStorage();
    } catch {
      // storage clear must not block leaving the session
    }
  };

  try {
    const result = await withTimeout(deps.signOutRemote('local'), timeoutMs);
    if (result.error) {
      clearStorageIfSameGeneration();
    }
  } catch {
    clearStorageIfSameGeneration();
  } finally {
    try {
      clearIdleTimeoutOverride();
    } catch {
      // tester override must not block leaving the session
    }
    try {
      deps.clearQueryCache();
    } catch {
      // cache clear must not block leaving the session
    }
  }
}

export function isAuthQueryError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { status?: number; code?: string; message?: string };
  if (e.status === 401 || e.status === 403) return true;
  if (e.code === 'PGRST301' || e.code === 'invalid_jwt' || e.code === 'session_not_found') {
    return true;
  }
  if (
    typeof e.message === 'string' &&
    /jwt expired|not authenticated|invalid jwt|401/i.test(e.message)
  ) {
    return true;
  }
  return false;
}
